import { createPortal } from "react-dom";
import Photos from "./Photos";
import { uploadGuidePhotos, getUneditedGuideAttachments } from "./photo_state";
import {
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import { Image } from "uikit-react";
import { InputContainer } from "uikit-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { AppContexts } from "../../../providers";
import { Form, FormRow, FormInputWrapper, Link } from "../../../ui";
import Tags from "../../common/Tags";
import MasterConfirm from "../../common/Confirm";
import MacrosReservationKeys from "../../common/MacroReservationKeys";
import VisibilityRules from "../../common/VisibilityRules";
import GuideContentAI from "../../common/GuideContentAI";
import {
  mergeSavedGuideSection,
  upsertGuideSection,
} from "./section_state";

const schema = yup.object().shape({
  title: yup.string().required(),
});

const defaultValues = {
  welcome_guide_question_id: 0,
  parent_id: 0,
  rental_id: 0,
  title: "",
  body: "",
  tags: [],
  attachment: "",
  video: "",
  icon: null,
  thumbnail: null,
  expanded: false,
};

export const MODAL_NAME = "master-property-guide-edit";

const PropertyGuideEdit = ({ onSaved, onSectionSaved }) => {
  const { setUser } = useContext(AppContexts.AuthContext);
  const { uploadFile, genUUID } = useContext(AppContexts.UtilContext);
  const {
    createSection,
    updateSection,
    selectedSection,
    setSelectedSection,
    rental,
    setRental,
  } = useContext(AppContexts.RentalsContext);
  const offcanvasRef = useRef(null);
  const savingRef = useRef(false);
  const uploadedPhotos = useRef(new Map());

  // const [attachments, setAttachments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoActionsTarget, setPhotoActionsTarget] = useState(null);
  const [attachPDF, setAttachPDF] = useState({});
  const [loading, setLoading] = useState(false);
  const [restrictions, setRestrictions] = useState([]);
  const [isGuideAiActive, setIsGuideAiActive] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    watch,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleBeforeHide = (event) => {
    if (event.target === offcanvasRef.current) {
      if (savingRef.current) { event.preventDefault(); return; }
      clearErrors();
      setSelectedSection({});
    }
  };

  const handleRestrictionsChange = (restrictions) => {
    setRestrictions(restrictions);
  }

  useEffect(() => {
    const offcanvasElement = UIkit.offcanvas(offcanvasRef.current);
    const offcanvasDOMElement = offcanvasElement.$el;
    offcanvasDOMElement.addEventListener("beforehide", handleBeforeHide);
    return () => {
      offcanvasDOMElement.removeEventListener("beforehide", handleBeforeHide);
      if (offcanvasDOMElement.classList.contains("uk-open")) {
        offcanvasElement.hide();
      }
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    setForm();
    setRestrictions(selectedSection?.restrictions || []);
  }, [selectedSection]);

  let isMaster = useMemo(() => {
    return !rental || !rental.rental_id;
  }, [rental]);

  const setForm = () => {
    clearErrors();
    setPhotos([]);
    uploadedPhotos.current.clear();
    setAttachPDF({});
    if (!selectedSection || Object.keys(selectedSection).length === 0) {
      clearForms();
    } else {
      Object.keys(defaultValues).forEach((formname) => {
        setValue(
          formname,
          selectedSection[formname]
            ? selectedSection[formname]
            : defaultValues[formname],
        );
      });

      let attachment = selectedSection.attachments?.find(
        (attachment) => attachment.type === "file",
      );
      setValue("attachment", attachment?.name || attachment?.original_name);

      let video = selectedSection.attachments?.find(
        (attachment) => attachment.type === "you_tube",
      );
      setValue("video", video?.url);

      setPhotos((selectedSection.attachments || []).filter((attachment) => attachment.type === "image"));

      let attachPDF = selectedSection.attachments?.find(
        (attachment) => attachment.type === "file",
      );
      if (attachPDF) setAttachPDF(attachPDF);
    }
  };

  const clearForms = useCallback(() => {
    Object.keys(defaultValues).forEach((formname) => {
      setValue(formname, defaultValues[formname]);
    });
  }, []);

  const handleRegister = async (formdata) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setLoading(true);
    let attachment_url;
    try {
      const attachments_buf = await uploadGuidePhotos(photos, uploadFile, genUUID, uploadedPhotos.current);
      attachments_buf.push(...getUneditedGuideAttachments(selectedSection.attachments));
      if (attachPDF?.file) {
        attachment_url = await uploadFile(
          attachPDF.file,
          `workorders/attachments/${genUUID()}`,
        );

        if (!attachment_url) {
          UIkit.notification("Failed Uploading PDF", "error");
          return;
        }
      }

      if (!!(attachPDF?.original_name || attachPDF?.name)) {
        if (attachPDF?.file) {
          attachments_buf.push({
            ...attachPDF,
            name:
              formdata.attachment || attachPDF.original_name || attachPDF.name,
            file: undefined,
            url: attachment_url,
          });
        } else if (attachPDF?.url) {
          attachments_buf.push({
            ...attachPDF,
            file: undefined,
            name:
              formdata.attachment || attachPDF.original_name || attachPDF.name,
          });
        }
      }

      if (formdata.video) {
        attachments_buf.push({
          type: "you_tube",
          name: "",
          original_name: "",
          url: formdata.video,
        });
      }

      formdata.attachments = attachments_buf;
      formdata.restrictions = restrictions;

      let plaindata = JSON.parse(JSON.stringify({
        suspended: selectedSection.suspended || false,
        placeholder: selectedSection.placeholder || "",
        ...formdata,
      }));

      let saveFn = updateSection;

      if (!!rental && !!rental.rental_id) {
        if (!selectedSection) {
          return console.log("Error updating section");
        }

        if (+plaindata.parent_id === 0 || +plaindata.rental_id === 0) {
          plaindata.parent_id = selectedSection.welcome_guide_question_id;
          plaindata.rental_id = rental.rental_id;

          saveFn = createSection;
        }
      }

      setLoading(true);
      let [section, sectionErr] = await saveFn({ ...plaindata });
      const mediaConfirmed = Array.isArray(section?.attachments) && attachments_buf.every((expected) =>
        section.attachments.some((attachment) => attachment.type === expected.type && attachment.url === expected.url));
      if (sectionErr !== null || section === null || !mediaConfirmed) {
        setLoading(false);
        UIkit.notification("Failed", "error");
      } else {
        const savedSection = mergeSavedGuideSection(
          selectedSection,
          plaindata,
          section,
        );

        onSectionSaved?.(savedSection);

        if (!!rental && !!rental.rental_id) {
          setRental((current) => ({
            ...current,
            welcome_guide: upsertGuideSection(
              current?.welcome_guide,
              savedSection,
            ),
          }));
        } else {
          setUser((current) => current ? {
            ...current,
            welcome_guide: upsertGuideSection(
              current.welcome_guide,
              savedSection,
            ),
          } : current);
        }
        setSelectedSection(savedSection);
        setLoading(false);
        savingRef.current = false;
        UIkit.offcanvas(offcanvasRef.current).hide();
        UIkit.notification("Updated Successfully", "success");
        onSaved?.();

        clearForms();
      }
    } catch (err) {
      console.log("Error", err);
      UIkit.notification("Failed", "error");
      setLoading(false);
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (savingRef.current) return;
    document.body.style.overflow = "";
    UIkit.offcanvas(offcanvasRef.current).hide();
  };

  const deletePDF = () => {
    setValue("attachments", null);
    setAttachPDF({});
  };

  const body = useMemo(() => getValues("body"), [watch("body")]);
  const title = useMemo(() => getValues("title"), [watch("title")]);
  const tags = useMemo(() => getValues("tags"), [watch("tags")]);
  const icon = useMemo(() => getValues("icon"), [watch("icon")]);

  const handleError = (err) => console.error(err);

  const bodyTextareaRef = useRef(null);
  const handleInsertMacro = (macro) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = (body || "").slice(0, start) + macro + (body || "").slice(end);
    setValue("body", newValue);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + macro.length;
    }, 0);
  };

  return createPortal(
    <>
      <div
        id={MODAL_NAME}
        ref={offcanvasRef}
        data-guide-editor-offcanvas
        className="uk-modal-full hms-modals uk-modal uk-open"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <div className="uk-modal-header guide-block-modal-header">
              <h2 className="uk-modal-title">Edit Block</h2>
              <button
                className="uk-offcanvas-close uk-close-large guide-block-modal-close"
                type="button"
                aria-label="Close Edit Block"
                uk-close="true"
              ></button>
            </div>
            <div className="uk-modal-body">
              <Form
                className="uk-grid-small"
                data-uk-grid
                onSubmit={handleSubmit(handleRegister, handleError)}
              >
                {isMaster && (
                  <FormRow>
                    <FormInputWrapper label="Title*" size="one">
                      <InputContainer>
                        <input
                          className={classnames("uk-input", {
                            error: errors["title"],
                          })}
                          type="text"
                          {...register("title")}
                        />
                      </InputContainer>
                    </FormInputWrapper>
                  </FormRow>
                )}
                {isMaster && (
                  <FormRow>
                    <FormInputWrapper label="Tags">
                      <div className="hms-add-tag">
                        <Tags
                          value={tags}
                          onChange={(editedTags) =>
                            setValue("tags", editedTags)
                          }
                        />
                      </div>
                    </FormInputWrapper>
                  </FormRow>
                )}
                <FormRow>
                  <FormInputWrapper label="Text" size="two">
                    <span className="hms-align-right">
                      <Link className="m-l-10">
                        <a
                          href="https://intercom.help/ruebarueapp/en/articles/9792129-style-shortcuts"
                          target="_blank"
                        >
                          View Styles
                        </a>
                      </Link>
                    </span>
                    <InputContainer>
                      <textarea
                        ref={bodyTextareaRef}
                        className={classnames("uk-textarea", {
                          error: errors.body,
                        })}
                        value={body || ""}
                        onChange={(event) =>
                          setValue("body", event.target.value)
                        }
                        rows="7"
                      />
                      <div className="large-msg-typing-btn">
                        <div className="uk-flex uk-flex-middle uk-flex-wrap sms-composer-actions">
                          <div hidden={isGuideAiActive}>
                            <MacrosReservationKeys
                              onMacroClick={handleInsertMacro}
                              portal
                            />
                          </div>
                          <GuideContentAI
                            promptId="guide-content-ai-edit-prompt"
                            title={title || ""}
                            value={body || ""}
                            onChange={(content) => setValue("body", content)}
                            onActiveChange={setIsGuideAiActive}
                            photos={photos}
                            photoActionsTarget={photoActionsTarget}
                            preparePhotos={(selected) => uploadGuidePhotos(selected, uploadFile, genUUID, uploadedPhotos.current)}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>
                <Photos photos={photos} onChange={setPhotos} disabled={loading || isGuideAiActive} />
                <div ref={setPhotoActionsTarget} className="guide-photo-ai uk-width-1-1" style={{ marginTop: 0, marginBottom: photos.length ? 20 : 0 }} />
                <FormRow>
                  <FormInputWrapper label="Video" size="two">
                    <InputContainer>
                      <input
                        className={classnames("uk-input", {
                          error: errors.video,
                        })}
                        type="text"
                        {...register("video")}
                        placeholder="YouTube, Vimeo or Loom Link"
                      />
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>
                <FormRow>
                  <FormInputWrapper label="PDF Label" size="one">
                    <InputContainer>
                      <input
                        className={classnames("uk-input", {
                          error: errors.label,
                        })}
                        type="text"
                        {...register("attachment")}
                      />
                    </InputContainer>
                  </FormInputWrapper>
                </FormRow>
                <FormRow>
                  <FormInputWrapper label="PDF">
                    <div className="uk-form-custom">
                      {!!(attachPDF.name || attachPDF.original_name) ? (
                        <>
                          <input
                            key="pdf_key"
                            type="file"
                            {...register("attachments")}
                            onChange={(evt) =>
                              setAttachPDF({
                                file: evt.target.files[0] || null,
                                name: evt.target.files[0]?.name,
                                original_name: evt.target.files[0]?.name,
                                type: "file",
                                url: "",
                              })
                            }
                          />
                          <span className="grey-text">
                            {attachPDF.original_name || attachPDF.original_name}
                          </span>
                        </>
                      ) : (
                        <>
                          <input
                            key="pdf_key"
                            type="file"
                            {...register("attachments")}
                            onChange={(evt) =>
                              setAttachPDF({
                                file: evt.target.files[0] || null,
                                name: evt.target.files[0]?.name,
                                original_name: evt.target.files[0]?.name,
                                type: "file",
                                url: "",
                              })
                            }
                          />
                          <div className="uk-placeholder hms-file-uploader uk-text-center">
                            <Image
                              src="/images/upload-pdf.svg"
                              className="hms-navbar"
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {!!attachPDF?.original_name && (
                      <MasterConfirm onDelete={deletePDF} />
                    )}
                  </FormInputWrapper>
                </FormRow>
                {isMaster && <FormRow>
                  <VisibilityRules
                    restrictions={restrictions}
                    onChangeBlockSettings={handleRestrictionsChange}
                    itemId={+selectedSection?.welcome_guide_question_id}
                    itemType={'welcome_guide_question'}
                  />
                </FormRow>}

                <input
                  className={classnames("uk-input", {
                    error: errors["welcome_guide_question_id"],
                  })}
                  type="hidden"
                  {...register("welcome_guide_question_id")}
                />
                <input
                  className={classnames("uk-input", {
                    error: errors["parent_id"],
                  })}
                  type="hidden"
                  {...register("parent_id")}
                />
                <input
                  className={classnames("uk-input", {
                    error: errors["rental_id"],
                  })}
                  type="hidden"
                  {...register("rental_id")}
                />

                <div className="uk-width-1-1 uk-modal-footer uk-text-right guide-block-modal-actions">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    type="submit"
                    disabled={loading || isGuideAiActive}
                  >
                    {loading ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="uk-button hms-btn"
                    onClick={handleClose}
                    disabled={loading}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default PropertyGuideEdit;
